
import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import Select from "react-select";
import {
  confirmDelete,
  createOrUpdateProject,
  getProjects as fetchProjects,
} from "../Projects/ProjectService";
import { getCouncils } from "../Councils/CouncilService";
import { getCompanies } from "../Companies/CompanyService";
import { getClients } from "../Clients/ClientService";
import { getProjectCategories } from "./ProjectCategoryService";
import { ConfirmDialog } from "primereact/confirmdialog";
import { getProject } from "../../services/ValitanApiService";
import { formatNumber, numberToWords } from "../Utility/numberUtils";
import { LoadingContext } from "../../../src/Pages/Utility/spinner/LoadingContext";

const ProjectListTables = () => {
  const { setIsLoading } = useContext(LoadingContext);
  document.title = "Projects | Valitan";

  const [projects, setProjects] = useState([]);
  const [councils, setCouncils] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [selectedProjectCategory, setSelectedProjectCategory] = useState(null);
  const [selectedCouncil, setSelectedCouncil] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [projectValue, setProjectValue] = useState("");
  const [populationHousehold, setPopulationHousehold] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  const loadProjects = useCallback(async (page = 0, size = 10) => {
    try {
      setIsLoading(true);
      const { projects, paginationInfo } = await fetchProjects(page, size);
      setProjects(projects || []);
      setPaginationInfo(paginationInfo || {
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        sort: {},
      });
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const loadProjectCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const { projectCategories } = await getProjectCategories();
      setProjectCategories(
        projectCategories.map((projectCategory) => ({
          value: projectCategory.id,
          label: projectCategory.name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching project categories:", error);
      setProjectCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const loadCouncils = useCallback(async () => {
    try {
      setIsLoading(true);
      const { councils } = await getCouncils();
      setCouncils(
        councils.map((council) => ({
          value: council.id,
          label: council.name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching councils:", error);
      setCouncils([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const { companies } = await getCompanies();
      setCompanies(
        companies.map((company) => ({
          value: company.id,
          label: company.name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const loadClients = useCallback(async () => {
    try {
      setIsLoading(true);
      const { clients } = await getClients();
      setClients(
        clients.map((client) => ({
          value: client.id,
          label: client.name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setLoading(true);
        await Promise.all([
          loadProjects(0, paginationInfo.pageSize),
          loadProjectCategories(),
          loadCouncils(),
          loadCompanies(),
          loadClients(),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };
    loadData();
  }, [
    paginationInfo.pageSize,
    loadProjects,
    loadProjectCategories,
    loadCouncils,
    loadCompanies,
    loadClients,
    setIsLoading,
  ]);

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadProjects(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadProjects(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      resetForm();
    }
  };

  const resetForm = () => {
    setProjectName("");
    setSelectedProjectCategory(null);
    setSelectedCouncil(null);
    setSelectedCompany(null);
    setSelectedClient(null);
    setDescription("");
    setCode("");
    setProjectValue("");
    setPopulationHousehold("");
    setStartDate("");
    setEndDate("");
    setStatus("");
    setErrors({});
    setEditingProjectId(null);
  };

  const openModalForEdit = async (id) => {
    setEditingProjectId(id);
    try {
      setIsLoading(true);
      const response = await getProject(id);
      const project = response?.data?.data;

      if (!project) {
        console.error("Project not found in API response.");
        return;
      }

      setProjectName(project.name || "");
      setSelectedProjectCategory(
        project.project_category
          ? { value: project.project_category.id, label: project.project_category.name }
          : null
      );
      setSelectedCouncil(
        project.council
          ? { value: project.council.id, label: project.council.name }
          : null
      );
      setSelectedCompany(
        project.company
          ? { value: project.company.id, label: project.company.name }
          : null
      );
      setSelectedClient(
        project.client
          ? { value: project.client.id, label: project.client.name }
          : null
      );
      setDescription(project.description || "");
      setCode(project.code || "");
      setProjectValue(project.project_value ? formatNumber(project.project_value) : "");
      setPopulationHousehold(
        project.population_household ? formatNumber(project.population_household) : ""
      );
      setStartDate(project.start_date?.split("T")[0] || "");
      setEndDate(project.end_date?.split("T")[0] || "");
      setStatus(project.status || "");

      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForDetails = async (id) => {
    try {
      setIsLoading(true);
      const response = await getProject(id);
      const project = response?.data?.data;

      if (!project) {
        console.error("Project not found in API response.");
        return;
      }

      setProjectName(project.name || "");
      setSelectedProjectCategory(
        project.project_category
          ? { value: project.project_category.id, label: project.project_category.name }
          : null
      );
      setSelectedCouncil(
        project.council
          ? { value: project.council.id, label: project.council.name }
          : null
      );
      setSelectedCompany(
        project.company
          ? { value: project.company.id, label: project.company.name }
          : null
      );
      setSelectedClient(
        project.client
          ? { value: project.client.id, label: project.client.name }
          : null
      );
      setDescription(project.description || "");
      setCode(project.code || "");
      setProjectValue(project.project_value ? formatNumber(project.project_value) : "");
      setPopulationHousehold(
        project.population_household ? formatNumber(project.population_household) : ""
      );
      setStartDate(project.start_date?.split("T")[0] || "");
      setEndDate(project.end_date?.split("T")[0] || "");
      setStatus(project.status || "");

      setDetailsModalOpen(true);
    } catch (error) {
      console.error("Error fetching project details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    createOrUpdateProject(
      event,
      projectName,
      selectedProjectCategory,
      selectedCouncil,
      selectedCompany,
      selectedClient,
      editingProjectId,
      loadProjects,
      toggleModal,
      setErrors,
      description,
      code,
      parseFloat(projectValue.replace(/,/g, "") || 0),
      parseFloat(populationHousehold.replace(/,/g, "") || 0),
      startDate,
      endDate,
      status
    );
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Project Management" breadcrumbItem="Projects" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Button
                    color="primary"
                    className="float-end"
                    onClick={toggleModal}
                  >
                    <i className="ri-add-line align-bottom me-1"></i> Add
                  </Button>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center">Loading...</div>
                  ) : (
                    <div className="table-responsive table-card mt-3">
                      <table className="table align-middle table-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Project</th>
                            <th>Council</th>
                            <th>Company</th>
                            <th>Client</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projects && projects.length > 0 ? (
                            projects.map((project, index) => (
                              <tr key={project.id}>
                                <td>{startIndex + index}</td>
                                <td>{project.name}</td>
                                <td>{project.council?.name || "N/A"}</td>
                                <td>{project.company?.name || "N/A"}</td>
                                <td>{project.client?.name || "N/A"}</td>
                                <td>
                                  <div
                                    className="btn-group"
                                    role="group"
                                    aria-label="Basic example"
                                  >
                                    <button
                                      id="TooltipTop"
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() => openModalForDetails(project.id)}
                                    >
                                      <i className="ri-eye-line"></i>
                                    </button>
                                    <button
                                      id="TooltipTop"
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() => openModalForEdit(project.id)}
                                    >
                                      <i className="ri-edit-box-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => confirmDelete(project.id, loadProjects)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Pagination
                    paginationInfo={paginationInfo}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    onPageChange={(pageNumber, pageSize) => loadProjects(pageNumber, pageSize)}
                    onPageSizeChange={(newPageSize) => {
                      setPaginationInfo((prev) => ({
                        ...prev,
                        pageSize: newPageSize,
                        pageNumber: 0, // Reset to the first page
                      }));
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmDialog />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop={"static"}>
        <ModalHeader toggle={toggleModal}>
          {editingProjectId ? "Edit Project" : "Add Project"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Project Name</Label>
              <Input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                invalid={!!errors.projectName}
              />
              {errors.projectName && <div className="invalid-feedback">{errors.projectName}</div>}
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Project Category</Label>
                  <Select
                    value={selectedProjectCategory}
                    onChange={setSelectedProjectCategory}
                    options={projectCategories}
                    placeholder="Select Project Category"
                    className={errors.projectCategory ? "is-invalid" : ""}
                  />
                  {errors.projectCategory && (
                    <div className="invalid-feedback">{errors.projectCategory}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Council</Label>
                  <Select
                    value={selectedCouncil}
                    onChange={setSelectedCouncil}
                    options={councils}
                    placeholder="Select Council"
                    className={errors.council ? "is-invalid" : ""}
                  />
                  {errors.council && <div className="invalid-feedback">{errors.council}</div>}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Company</Label>
                  <Select
                    value={selectedCompany}
                    onChange={setSelectedCompany}
                    options={companies}
                    placeholder="Select Company"
                    className={errors.company ? "is-invalid" : ""}
                  />
                  {errors.company && <div className="invalid-feedback">{errors.company}</div>}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Client</Label>
                  <Select
                    value={selectedClient}
                    onChange={setSelectedClient}
                    options={clients}
                    placeholder="Select Client"
                    className={errors.client ? "is-invalid" : ""}
                  />
                  {errors.client && <div className="invalid-feedback">{errors.client}</div>}
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                invalid={!!errors.description}
              />
              {errors.description && <div className="invalid-feedback">{errors.description}</div>}
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Project Code</Label>
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    invalid={!!errors.code}
                  />
                  {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Project Value (Tsh)</Label>
                  <Input
                    type="text"
                    value={projectValue}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (!isNaN(rawValue)) {
                        setProjectValue(formatNumber(rawValue));
                      }
                    }}
                    invalid={!!errors.projectValue}
                  />
                  {errors.projectValue && (
                    <div className="invalid-feedback">{errors.projectValue}</div>
                  )}
                  {projectValue && (
                    <small className="text-muted">
                      {numberToWords(projectValue.replace(/,/g, ""))} Tanzanian Shillings
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Population Household</Label>
                  <Input
                    type="text"
                    value={populationHousehold}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (!isNaN(rawValue)) {
                        setPopulationHousehold(formatNumber(rawValue));
                      }
                    }}
                  />
                  {populationHousehold && (
                    <small className="text-muted">
                      {numberToWords(populationHousehold.replace(/,/g, ""))} People/Households
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Select status</option>
                    <option value="PLANNED">Planned</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Button color="primary" type="submit" className="float-end">
              {editingProjectId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={detailsModalOpen}
        toggle={() => setDetailsModalOpen(false)}
        backdrop={"static"}
        className="modal-lg"
      >
        <ModalHeader toggle={() => setDetailsModalOpen(false)}>
          <span style={{ marginRight: "2em" }}>
            <strong>Project Details</strong>
          </span>
          {projectName}
        </ModalHeader>
        <ModalBody>
          <Row className="d-flex">
            <div className="col-md-12">
              <div className="plan-features mt-2 mb-3">
                <p className="d-flex align-items-center">
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-18 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Name:</strong>
                  </span>
                  {projectName}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Project Category:</strong>
                  </span>
                  {selectedProjectCategory?.label || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Council:</strong>
                  </span>
                  {selectedCouncil?.label || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Company:</strong>
                  </span>
                  {selectedCompany?.label || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Client:</strong>
                  </span>
                  {selectedClient?.label || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Description:</strong>
                  </span>
                  {description || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Project Code:</strong>
                  </span>
                  {code || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Project Value:</strong>
                  </span>
                  {projectValue ? `${ projectValue } TZS` : "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Population Household:</strong>
                  </span>
                  {populationHousehold || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Status:</strong>
                  </span>
                  {status || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>Start Date:</strong>
                  </span>
                  {startDate || "N/A"}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>
                  <span style={{ marginRight: "2em" }}>
                    <strong>End Date:</strong>
                  </span>
                  {endDate || "N/A"}
                </p>
              </div>
            </div>
          </Row>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ProjectListTables;
